import { PagedResponse } from "../../../utils/helpers";
import { FlatList, ListRenderItem, StyleSheet, View, Text, TouchableOpacity } from "react-native";
import { BaseFilters } from "../../web/hooks/useFilters";
import React from "react";
import { DataTable } from "react-native-paper";
import { EmptyList } from "../../../components/flatlist/EmptyList";
import { Checkbox, Pagination, Tooltip } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";

interface CoreTableHeader {
  text: string;
  hidden?: boolean;
  flex?: number;
  sortedName?: string;
}

interface CoreTableProps<T> {
  headers: CoreTableHeader[];
  data: PagedResponse<T>;
  renderItem: (props: { item: T; index: number; headers: CoreTableHeader[] }) => React.ReactElement;
  keyExtractor: (data: T) => string;
  isLoading?: boolean;
  filters: BaseFilters;
  setFilter: (key: keyof BaseFilters, value: any) => void;

  showCheckbox?: boolean;
  isSelectAllChecked?: boolean;
  onSelectAll?: () => void;
  onClearAll?: () => void;
  selectedItems?: () => void;
}

export function CoreTable<T>({
  headers,
  data,
  renderItem,
  keyExtractor,
  isLoading,
  filters,
  setFilter,
  showCheckbox = false,
  isSelectAllChecked,
  onSelectAll,
  onClearAll
}: CoreTableProps<T>) {
  const { pageNumber, pageSize, sortedColumn, sortedColumnAscending } = filters;

  const sortHeader = (header: CoreTableHeader) => {
    const column = header.sortedName;
    if (!column) return;

    if (sortedColumn.toLowerCase() === column.toLowerCase()) {
      setFilter("sortedColumnAscending", !sortedColumnAscending);
    } else {
      setFilter("sortedColumn", column);
    }
  };

  const previewData = isLoading ? [] : data?.list ?? [];
  const sortHeaderOrder = () => (sortedColumnAscending ? "ascending" : "descending");

  const handleOnPaginationChange = (pageNumber: number, pageSize: number) => {
    setFilter("pageNumber", pageNumber);
    setFilter("pageSize", pageSize);
  };

  const handleSelectAllToggle = () => {
    if (isSelectAllChecked) onClearAll?.();
    else onSelectAll?.();
  };

  return (
    <DataTable style={styles.table}>
      {/* HEADER */}
      <View style={[styles.header]}>
        {(headers ?? [])
          .filter((h) => !h.hidden)
          .map((header, index) => {
            const sortDirection =
              header.sortedName?.toLowerCase() !== sortedColumn?.toLowerCase()
                ? null
                : sortHeaderOrder();

            if (header.text === "Basket" && showCheckbox) {
              return (
                <View key={index} style={{ flex: header.flex ?? 1, justifyContent: "center" }}>
                  <Checkbox
                    style={{ padding: 10 }}
                    checked={isSelectAllChecked}
                    onClick={handleSelectAllToggle}
                  />
                </View>
              );
            }

            return (
              <TouchableOpacity
                key={`${header.text}-${index}`}
                onPress={() => sortHeader(header)}
                activeOpacity={0.7}
                style={{ flex: header.flex ?? 1 }}
              >
                <View style={{ flexDirection: "row", alignItems: "center", padding: 10 }}>
                  <Text style={{ fontWeight: "bold" }}>{header.text}</Text>
                  {sortDirection === "ascending" && <UpOutlined style={{ marginLeft: 4 }} />}
                  {sortDirection === "descending" && <DownOutlined style={{ marginLeft: 4 }} />}
                </View>
              </TouchableOpacity>
            );
          })}
      </View>

      {/* DATA ROWS */}
      <FlatList
        style={{ flex: 1 }}
        data={previewData}
        keyExtractor={keyExtractor}
        renderItem={({ item, index }) => renderItem({ item, index, headers })}
        ListEmptyComponent={
          <EmptyList
            isLoading={isLoading}
            emptyText="There are no results. Try changing your search filters."
          />
        }
      />

      {/* PAGINATION */}
      <View style={styles.pagination}>
        <Pagination
          total={data?.pagination.totalCount ?? 0}
          showTotal={(total) => `Total ${total} items`}
          defaultPageSize={pageSize}
          defaultCurrent={pageNumber}
          onChange={handleOnPaginationChange}
        />
      </View>
    </DataTable>
  );
}

/* --------------------
 * ROW COMPONENT
 * -------------------- */
CoreTable.Row = ({ children, style }: { children: any; style?: any }) => {
  return <View style={[styles.row, style]}>{children}</View>;
};

/* --------------------
 * CELL COMPONENT
 * -------------------- */
CoreTable.Cell = ({
  children,
  style,
  tooltip = true
}: {
  children: any;
  style?: any;
  tooltip?: boolean;
}) => {
  const mergedStyle = {
    flex: style?.flex ?? 1,
    alignSelf: "stretch" as const,
    flexWrap: "wrap" as const
  };
  const textstyle = { 
    flexWrap: "wrap" as const ,
    overflowWrap: "break-word" as const,
    wordBreak: "break-word" as const
  };

  const content = (
    <Text numberOfLines={undefined} style={{ padding: 10, ...textstyle }}>
      {children}
    </Text>
  );

  if (!tooltip) return <View style={mergedStyle}>{content}</View>;

  return (
    <Tooltip title={children}>
      <View style={mergedStyle}>{content}</View>
    </Tooltip>
  );
};

/* --------------------
 * STYLES
 * -------------------- */
const styles = StyleSheet.create({
  table: {
    backgroundColor: "#FFF",
    borderWidth: 1,
    borderColor: "#CECECE",
    borderRadius: 6,
    flex: 1
  },
  header: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "flex-start",
    paddingHorizontal: 0,
    marginRight: 16
  },
  row: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "flex-start"
  },
  pagination: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: 8
  }
});
